import { memo, useMemo } from "react";
import {
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { BEAT_COLORS } from "components/field/RadarBeatSignal";
import { Key, LIVE_COLORS } from "components/field/RadarSpectrum";
import { fieldTime } from "utils/fieldFormat";
import { ACCURACY_LIMIT_MM as LIMIT, LAB_COLORS, accuracyPoints, formatCm } from "utils/radarLab";

// One colour per estimator, in the accuracy table's keys and this chart alike:
// ours in the field pages' teal, v1 in the beat signal's blue, the spectrum and
// the radar's own reading as the lab's charts drew them before.
export const ESTIMATOR_COLORS = {
  v2: LIVE_COLORS.ours,
  v1: BEAT_COLORS.beat,
  spectrum: LAB_COLORS.spectrum,
  radar: LIVE_COLORS.radar,
};
const TICKS = [-LIMIT, (-2 * LIMIT) / 3, -LIMIT / 3, 0, LIMIT / 3, (2 * LIMIT) / 3, LIMIT];
const AXIS_TICK = { fontSize: 10, fill: "#6d8490" };
const signed = (value, digits = 0) => `${value > 0 ? "+" : value < 0 ? "−" : ""}${Math.abs(value).toFixed(digits)}`;
const clock = (ms) =>
  new Intl.DateTimeFormat("en-SG", {
    timeZone: "Asia/Singapore",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(ms));

// The ruler's levels on whole steps (5, 2, 1 or 0.5 cm, by their spread).
export function levelAxis(levels) {
  const low = Math.min(...levels);
  const high = Math.max(...levels);
  const spread = high - low;
  const step = spread > 12 ? 5 : spread > 5 ? 2 : spread > 2 ? 1 : 0.5;
  const start = Math.floor(low / step) * step;
  const end = Math.max(start + step, Math.ceil(high / step) * step);
  const ticks = [];
  for (let v = start; v <= end + 1e-9; v += step) ticks.push(Math.round(v * 10) / 10);
  return { domain: [start, end], ticks };
}

// A point is a dot; one whose error is beyond the axis sits on its edge as a
// triangle pointing the way the error went. The clear ring behind it is the
// hover target.
function Mark({ cx, cy, fill, payload }) {
  if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null;
  const beyond = payload?.beyond || 0;
  return (
    <g>
      <circle cx={cx} cy={cy} r={8} fill="transparent" />
      {beyond ? (
        <path
          d={`M${cx},${cy - beyond * 6} L${cx + 5},${cy + beyond * 3} L${cx - 5},${cy + beyond * 3} Z`}
          fill={fill}
          stroke="white"
          strokeWidth={1}
        />
      ) : (
        <circle cx={cx} cy={cy} r={4} fill={fill} stroke="white" strokeWidth={1.5} />
      )}
    </g>
  );
}

function PointTip({ active, payload }) {
  const point = active ? payload?.[0]?.payload : null;
  if (!point) return null;
  return (
    <div className="field-burst-tip">
      <b>
        {signed(point.error, 1)} mm{point.beyond ? ", off the scale" : ""}
      </b>
      <span>{point.name}</span>
      <span>
        Ruler {formatCm(point.level, 2)} · {fieldTime(point.t)} SGT
      </span>
    </div>
  );
}

// Each estimator's error against the ruler camera (mm, through its own fitted
// mapping), by the ruler's level (x "level": where in the tank it errs) or
// over time (x "time"). rows are the accuracy table's ({key, name, prefix});
// errors beyond ±LIMIT sit on the edge, counted in the key; 0 is the ruler.
const ErrorChart = memo(function ErrorChart({ rows, pairs, x = "level" }) {
  const series = useMemo(
    () =>
      (rows || []).map((row) => {
        const points = accuracyPoints(pairs, row.prefix)
          .map((p) => ({ ...p, ms: Date.parse(p.t), name: row.name }))
          .filter((p) => x === "level" || Number.isFinite(p.ms));
        return {
          ...row,
          color: ESTIMATOR_COLORS[row.key],
          points,
          beyond: points.filter((p) => p.beyond).length,
        };
      }),
    [rows, pairs, x],
  );
  const values = series.flatMap((s) => s.points.map((p) => (x === "time" ? p.ms : p.level)));
  if (!values.length) return null;
  const axis =
    x === "time"
      ? { dataKey: "ms", domain: ["dataMin", "dataMax"], ticks: undefined, format: clock }
      : { dataKey: "level", ...levelAxis(values), format: (v) => `${v}` };
  const anyBeyond = series.some((s) => s.beyond);
  return (
    <figure className="field-lab-accuracy-figure">
      <div className="field-lab-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 12, right: 16, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="#e8eff1" />
            <XAxis
              type="number"
              dataKey={axis.dataKey}
              domain={axis.domain}
              ticks={axis.ticks}
              tickFormatter={axis.format}
              tick={AXIS_TICK}
            />
            <YAxis
              type="number"
              dataKey="mm"
              domain={[-LIMIT, LIMIT]}
              ticks={TICKS}
              tickFormatter={(v) => signed(v)}
              tick={AXIS_TICK}
              width={40}
            />
            <ReferenceLine y={0} stroke={LAB_COLORS.ruler} strokeWidth={1.5} />
            <Tooltip content={<PointTip />} cursor={false} isAnimationActive={false} />
            {/* Ours drawn last, on top. Each series its own dataKey: a hovered point's tooltip is found by it
                (the axes' dataKeys place the points). */}
            {[...series].reverse().map((s) => (
              <Scatter
                key={s.key}
                dataKey={s.key}
                name={s.name}
                data={s.points}
                fill={s.color}
                shape={Mark}
                isAnimationActive={false}
              />
            ))}
          </ScatterChart>
        </ResponsiveContainer>
      </div>
      <figcaption>
        <ul className="field-radar-legend" aria-label="Accuracy chart key">
          {series.map((s) => (
            <Key key={s.key} kind="dot" color={s.color}>
              {s.name}
              {s.beyond ? ` · ${s.beyond.toLocaleString()} beyond ±${LIMIT} mm` : ""}
            </Key>
          ))}
          <Key color={LAB_COLORS.ruler}>The ruler (no error)</Key>
          {anyBeyond && (
            <li>
              <svg className="field-lab-edge-key" width="20" height="10" viewBox="0 0 20 10" aria-hidden="true">
                <path d="M5,1 L9,8 L1,8 Z" />
                <path d="M15,9 L19,2 L11,2 Z" />
              </svg>
              Beyond ±{LIMIT} mm, on the edge
            </li>
          )}
        </ul>
      </figcaption>
    </figure>
  );
});

export default ErrorChart;
